/**
 * ログイン不要の端末証明書ダウンロード(/dc)。
 *
 * **トークンは URL の fragment(`#`)で受け取る**(fix1 の高 1)。fragment は
 * ブラウザがサーバーへ送らないので、Caddy / uvicorn のアクセスログにも
 * リファラにも残らない。読んだら `history.replaceState` でその場で消し、
 * 以降はメモリ(state)だけで持つ。API へはパスではなく **body** で渡す。
 *
 * **開くだけでは消費しない。** メールの安全装置やチャットのリンク展開が URL を
 * 先読みしても、消えるのは本人がボタンを押した `POST /api/idp/dc/download` の
 * ときだけ(確認は `POST /api/idp/dc/check` で、何も消さない)。
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { Alert, Button, Card, Result, Space, Spin, Typography } from "antd";
import { useState } from "react";

import { ApiError } from "../lib/api";
import { useApi } from "../lib/useApi";

/** `#` の中身を 1 度だけ読み、読んだそばから URL を書き換えて消す。 */
function takeHashToken(): string {
  if (typeof window === "undefined") return "";
  const raw = window.location.hash.replace(/^#/, "");
  if (!raw) return "";
  let token = raw;
  try {
    token = decodeURIComponent(raw);
  } catch {
    // 壊れた %エンコードはそのまま渡す(形が違えば 400 / 404 になる)
  }
  try {
    window.history.replaceState(
      null,
      "",
      window.location.pathname + window.location.search,
    );
  } catch {
    // replaceState が使えない環境でも続ける(消せないだけ)
  }
  return token;
}

export function DeviceCertDownload() {
  const api = useApi();
  // 初期化の 1 回だけ読む。再描画で URL を読み直しても、もう `#` は消えている
  const [token] = useState(takeHashToken);
  const [done, setDone] = useState(false);

  const check = useQuery({
    queryKey: ["device-cert-check"],
    queryFn: () => api.deviceCertCheck(token),
    enabled: token !== "",
    retry: false,
    refetchOnWindowFocus: false,
  });

  const get = useMutation({
    mutationFn: async () => {
      const { blob, filename } = await api.deviceCertDownload(token);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    },
    onSuccess: () => setDone(true),
  });

  // 429 は「このリンクが駄目」ではなく「今は混んでいる」。同じ文面で返すと、
  // まだ生きているリンクを本人が捨ててしまう(fix2 の低 7)
  const tooMany = get.error instanceof ApiError && get.error.status === 429;

  const frame = (children: React.ReactNode) => (
    <div style={{ maxWidth: 640, margin: "48px auto", padding: "0 16px" }}>
      <Typography.Title level={4}>端末証明書のダウンロード</Typography.Title>
      {children}
    </div>
  );

  if (token === "")
    return frame(
      <Result
        status="404"
        title="このリンクは使えない"
        subTitle="メールに書かれた URL を、末尾まで全部コピーして開き直してください。"
      />,
    );

  if (check.isLoading) return frame(<Spin data-testid="dc-loading" />);

  if (check.isError)
    return frame(
      <Result
        status="404"
        title="このリンクは使えない"
        subTitle="期限切れ・取得済み・取り消し済みのいずれかです。発行した担当者に作り直しを頼んでください。"
      />,
    );

  if (done)
    return frame(
      <Result
        status="success"
        title="ダウンロードを始めた"
        subTitle="このリンクはもう使えません。ファイルを開くパスワードは別の経路でお渡ししています。"
      />,
    );

  return frame(
    <Card size="small">
      <Space direction="vertical" style={{ width: "100%" }}>
        <Typography.Paragraph>
          このリンクは<strong>一度だけ</strong>使えます。下のボタンを押すとファイルの
          ダウンロードが始まり、リンクはその場で無効になります。
        </Typography.Paragraph>
        <Typography.Paragraph>
          ファイルを開くパスワードは別の経路でお渡ししています。
        </Typography.Paragraph>
        {get.isError && (
          <Alert
            type={tooMany ? "warning" : "error"}
            showIcon
            message={
              tooMany ? "今は受け付けられない" : "ダウンロードできなかった"
            }
            description={
              tooMany
                ? "短いあいだに要求が集中しています。このリンクはまだ使えるので、しばらく待ってからもう一度押してください。"
                : "リンクの期限が切れているか、すでに使われています。発行した担当者に作り直しを頼んでください。"
            }
          />
        )}
        <Button
          type="primary"
          data-testid="dc-download"
          loading={get.isPending}
          onClick={() => get.mutate()}
        >
          ダウンロードする
        </Button>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          ログインは要りません。この画面を再読み込みすると、メールのリンクを開き直す必要があります(URL の # から先はここで消しているため)。
        </Typography.Text>
      </Space>
    </Card>,
  );
}
